import {
  AddIcon,
  Button,
  ButtonGroup,
  DeleteIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  ImportIcon,
  Row,
  Table,
  TableColumns,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { FactorType, SortDirection, useTeachingLoadFactorsQuery } from '../../../types/planung-graphql-client-defs';
import { TeachingLoadFactorType } from '../types';
import styles from './FactorsTable.module.scss';

type FactorsTableProps = {
  loading: boolean;
  onAdd: (type: FactorType) => void;
  onEdit: (uuid: string, type: FactorType) => void;
  onInherit: () => void;
  onToggleActive: (elements: { uuid: string; active: boolean }[]) => void;
  onDelete: (uuids: string[]) => void;
};

export const FactorsTable = observer(
  ({ loading, onAdd, onEdit, onInherit, onToggleActive, onDelete }: FactorsTableProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
    const context = useMemorizedCacheTag('TEACHINGLOADFACTOR');

    const { columnVisibility, saveColumnVisibility } = useHiddenColumns('subjects-list');
    const { sorting, saveSorting } = useColumnsSort('subjects-list');
    const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

    const [{ data: factorsData }] = useTeachingLoadFactorsQuery({
      variables: {
        where: { schoolYear: { uuid: currentSchoolYear?.uuid } },
        options: {
          sort: [{ active: SortDirection.Desc, factorType: SortDirection.Desc }],
        },
      },
      context,
    });

    const columns = useMemo((): TableColumns<TeachingLoadFactorType>[] => {
      return [
        {
          header: t('common.type'),
          id: 'factorName',
          accessorKey: 'factorName',
          size: 70,
        },
        {
          header: t('classes.level.full'),
          id: 'grades',
          accessorKey: 'grades',
          size: 75,
          cell: ({ row }) =>
            row.original.grades.length === 0 ? (
              <div className={styles.all}>{t('common.all')}</div>
            ) : (
              row.original.grades.join(', ')
            ),
          accessorFn: ({ grades }) => (grades.length === 0 ? t('common.all') : grades.join(', ')),
        },
        {
          header: t('gradeGroups.title', { count: 1 }),
          id: 'gradeGroups',
          accessorKey: 'gradeGroups',
          size: 75,
          cell: ({ row }) =>
            row.original.gradeGroup.length === 0 ? (
              <div className={styles.all}>{t('common.all')}</div>
            ) : (
              row.original.gradeGroup.map((s) => s.name).join(', ')
            ),
          accessorFn: ({ gradeGroup }) =>
            gradeGroup.length === 0 ? t('common.all') : gradeGroup.map((s) => s.name).join(', '),
        },
        {
          header: t('subject.name'),
          id: 'subject',
          accessorKey: 'subject',
          size: 200,
          cell: ({ row }) =>
            row.original.subject.length === 0 ? (
              <div className={styles.all}>{t('common.all')}</div>
            ) : (
              row.original.subject.map((s) => s.name).join(', ')
            ),
          accessorFn: ({ subject }) => (subject.length === 0 ? t('common.all') : subject.map((s) => s.name).join(', ')),
        },
        {
          header: t('subjectContainer.title', { count: 1 }),
          id: 'subjectContainer',
          accessorKey: 'subjectContainer',
          size: 150,
          cell: ({ row }) =>
            row.original.subjectContainer.length === 0 ? (
              row.original.factorType === FactorType.Time ? (
                <div className={styles.all}>{t('common.all')}</div>
              ) : (
                '-'
              )
            ) : (
              row.original.subjectContainer.map((s) => s.shortName).join(', ')
            ),
          accessorFn: ({ factorType, subjectContainer }) =>
            subjectContainer.length === 0
              ? factorType === FactorType.Time
                ? t('common.all')
                : '-'
              : subjectContainer.map((s) => s.shortName).join(', '),
        },
        {
          header: t('common.timeGrids'),
          id: 'timeGrids',
          size: 150,
          cell: ({ row }) =>
            row.original.timeGridEntries.length === 0 ? (
              row.original.factorType === FactorType.Base ? (
                <div className={styles.all}>{t('common.all')}</div>
              ) : (
                '-'
              )
            ) : (
              row.original.timeGridEntries.map((t) => t.name).join(', ')
            ),
          accessorFn: ({ factorType, timeGridEntries }) =>
            timeGridEntries.length === 0
              ? factorType === FactorType.Base
                ? t('common.all')
                : '-'
              : timeGridEntries.map((t) => t.name).join(', '),
        },
        {
          header: t('persons.title', { count: 1 }),
          id: 'teacher',
          accessorKey: 'teacher',
          size: 100,
          cell: ({ row }) =>
            row.original.teachers.length === 0 ? (
              row.original.factorType === FactorType.Person ? (
                <div className={styles.all}>{t('common.all')}</div>
              ) : (
                '-'
              )
            ) : (
              row.original.teachers.map((s) => s.shortName).join(', ')
            ),
          accessorFn: ({ factorType, teachers }) =>
            teachers.length === 0
              ? factorType === FactorType.Person
                ? t('common.all')
                : '-'
              : teachers.map((s) => s.shortName).join(', '),
        },
        {
          header: t('factors.title', { count: 1 }),
          accessorKey: 'valueRaw',
          id: 'value',
          size: 75,
          cell: ({ row }) => row.original.valueRaw,
          accessorFn: ({ valueRaw }) => (valueRaw ? valueRaw.toString() : '-'),
        },
        {
          header: t('factors.titlePartial'),
          accessorKey: 'valuePartialRaw',
          id: 'valuePartial',
          size: 75,
          cell: ({ row }) => row.original.valuePartial,
          accessorFn: ({ valuePartial }) => (valuePartial ? valuePartial.toString() : '-'),
        },
        {
          header: t('common.comment'),
          accessorKey: 'comment',
          id: 'comment',
          size: 200,
        },
        {
          header: t('common.active.full'),
          accessorKey: 'active',
          meta: {
            filterName: t('common.active.full'),
            tooltip: t('common.active.full'),
          },
          id: 'active',
          type: 'active',
        },
      ];
    }, [t]);

    const data: TeachingLoadFactorType[] = useMemo(() => {
      return (
        factorsData?.teachingLoadFactors.map((factor) => {
          let factorName;
          switch (factor.factorType) {
            case FactorType.Time:
              factorName = t('factors.time');
              break;
            case FactorType.Base:
              factorName = t('factors.base');
              break;
            case FactorType.Person:
              factorName = t('factors.person');
              break;
          }
          return { ...factor, factorName };
        }) ?? []
      );
    }, [t, factorsData?.teachingLoadFactors]);

    function createBulkEditItems(rows: Row<TeachingLoadFactorType>[]): DropdownMenuItem[] {
      return [
        {
          label: t('common.activate'),
          subContent: [
            {
              label: t('common.activateAll'),
              onClick: () => {
                onToggleActive(
                  rows.map(({ original }) => {
                    return { uuid: original.uuid, active: true };
                  }),
                );
                clearSelection();
              },
            },
            {
              label: t('common.deactivateAll'),
              onClick: () => {
                onToggleActive(
                  rows.map(({ original }) => {
                    return { uuid: original.uuid, active: false };
                  }),
                );
                clearSelection();
              },
            },
          ],
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.delete'),
          color: 'error',
          onClick: () => {
            onDelete(rows.map(({ original }) => original.uuid));
            clearSelection();
          },
        },
      ];
    }

    return (
      <Table<TeachingLoadFactorType>
        className={styles['factors-table']}
        loading={loading}
        showBorderRadius
        showShadow
        canScroll
        isDesktop={true}
        isOnWhite={false}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        sorting={sorting}
        onSortingChange={saveSorting}
        showSort
        columns={columns}
        data={data}
        showSelect
        showVisibility
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('lessonUnit.factors')} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
          filename: `${t('lessonUnit.factors')}_${currentSchoolYear?.shortName}`,
        }}
        emptyStateSettings={{
          showButton: true,
          buttonIcon: <ImportIcon />,
          buttonText: t('common.inheritFromPreviousYear'),
          onButtonClick: () => onInherit(),
        }}
        showActionBar
        actionBarSettings={{
          showExpertFilter: true,
          showPrintButton: true,
          showBulkEdit: true,
          extendedActionsRight: (
            <>
              <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onInherit}>
                {t('common.inherit')}
              </Button>
              <Dropdown
                noPadding
                trigger={
                  <Button hierarchy='tertiary' icon={<AddIcon />}>
                    {t('common.add')}
                  </Button>
                }
              >
                <DropdownMenu
                  data={[
                    {
                      label: t('factors.base'),
                      onClick: () => onAdd(FactorType.Base),
                    },
                    {
                      label: t('factors.time'),
                      onClick: () => onAdd(FactorType.Time),
                    },
                    {
                      label: t('factors.person'),
                      onClick: () => onAdd(FactorType.Person),
                    },
                  ]}
                />
              </Dropdown>
            </>
          ),
        }}
        bulkEditDropdownContent={(rows) => <DropdownMenu data={createBulkEditItems(rows)} />}
        lastColWidth='68px'
        lastCol={({ original }) => {
          return (
            <ButtonGroup>
              <Button
                hierarchy='tertiary'
                onClick={() => onEdit(original.uuid, original.factorType)}
                icon={<EditIcon className='small' />}
              />
              <Button
                hierarchy='tertiary'
                onClick={() => onDelete([original.uuid])}
                icon={<DeleteIcon className='small' />}
              />
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
